/**
 * Layer Control Bridge
 *
 * Exposes React layer control functionality to legacy JavaScript code.
 * Provides a unified interface for managing the Leaflet layer control.
 *
 * Usage from legacy code:
 *   window.CFN.layerControl.refresh();
 *   window.CFN.layerControl.isLayerVisible(layerDef);
 *   window.CFN.layerControl.setLayerVisibility(layerId, visible);
 */
// Storage key for user preferences
const STORAGE_KEY = 'cfn_layer_selector_preferences';
/**
 * Load user preferences from localStorage
 */
function loadPreferences() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    }
    catch (error) {
        console.warn('Error loading layer selector preferences:', error);
        return {};
    }
}
/**
 * Save user preferences to localStorage
 */
function savePreferences(preferences) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    }
    catch (error) {
        console.warn('Error saving layer selector preferences:', error);
    }
}
/**
 * Get the layer key from a layer definition
 */
function getLayerKey(layerDef) {
    return layerDef.layer_key || String(layerDef.id) || layerDef.name || null;
}
/**
 * Check if a layer should be visible in the selector
 */
function isLayerVisibleInSelector(layerDef) {
    const preferences = loadPreferences();
    const layerId = getLayerKey(layerDef);
    if (!layerId)
        return true;
    // Check for user override first
    if (preferences.hasOwnProperty(layerId)) {
        return preferences[layerId];
    }
    // Fall back to database default
    return layerDef.default_visible_in_selector !== false;
}
/**
 * Set user preference for a layer's visibility in selector
 */
function setLayerVisibility(layerId, visible) {
    const preferences = loadPreferences();
    preferences[layerId] = visible;
    savePreferences(preferences);
    // Trigger custom event for UI updates
    window.dispatchEvent(new CustomEvent('layerSelectorPreferencesChanged', {
        detail: { layerId, visible }
    }));
    // Immediately refresh the layer control to apply changes
    refreshLayerControl();
}
/**
 * Reset user preferences for a layer
 */
function resetLayerVisibility(layerId) {
    const preferences = loadPreferences();
    delete preferences[layerId];
    savePreferences(preferences);
    window.dispatchEvent(new CustomEvent('layerSelectorPreferencesChanged', {
        detail: { layerId, reset: true }
    }));
    // Immediately refresh the layer control to apply changes
    refreshLayerControl();
}
/**
 * Clear all user preferences
 */
function clearAllPreferences() {
    savePreferences({});
    window.dispatchEvent(new CustomEvent('layerSelectorPreferencesChanged', {
        detail: { clearAll: true }
    }));
    // Immediately refresh the layer control to apply changes
    refreshLayerControl();
}
/**
 * Get all user preferences
 */
function getAllPreferences() {
    return { ...loadPreferences() };
}
/**
 * Refresh the layer control based on current preferences
 * This delegates to the existing window.refreshLayerControl if available
 */
async function refreshLayerControl() {
    const windowAny = window;
    // Use existing refreshLayerControl if available
    if (typeof windowAny.refreshLayerControl === 'function') {
        windowAny.refreshLayerControl();
        return;
    }
    console.warn('[layerControlBridge] refreshLayerControl not available on window');
}
/**
 * Register the layer control bridge with window.CFN
 */
export function registerLayerControlBridge() {
    if (typeof window !== 'undefined') {
        const CFN = window.CFN || {};
        window.CFN = CFN;
        CFN.layerControl = {
            refresh: refreshLayerControl,
            isLayerVisible: isLayerVisibleInSelector,
            setLayerVisibility,
            resetLayerVisibility,
            clearAllPreferences,
            getAllPreferences,
            getLayerKey,
        };
        console.log('✅ Layer control bridge initialized');
        console.log('   Available: window.CFN.layerControl.refresh()');
        console.log('   Available: window.CFN.layerControl.isLayerVisible(layerDef)');
        console.log('   Available: window.CFN.layerControl.setLayerVisibility(layerId, visible)');
    }
}
export { isLayerVisibleInSelector, setLayerVisibility, resetLayerVisibility, clearAllPreferences, getAllPreferences, refreshLayerControl, getLayerKey, };
